import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Icon } from './Layout'
import { getActiveTasks, getMyTaskSubmissions, submitTaskProof } from '../services/tasks'
import { useAuth } from '../context/AuthContext'
import { getLevelForCis, formatMultiplier } from '../utils/cisLevels'

const TYPE_META = {
  like: { label: 'Like', icon: 'favorite' },
  comment: { label: 'Comment', icon: 'chat_bubble' },
  retweet: { label: 'Repost', icon: 'repeat' },
  follow: { label: 'Follow', icon: 'person_add' },
  post_keyword: { label: 'Post', icon: 'edit_note' },
  custom: { label: 'Task', icon: 'task_alt' },
}

function StateBadge({ state, cooldownEndsAt }) {
  if (state === 'pending') {
    return (
      <span className="px-2 py-0.5 border-2 border-on-background/30 font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">
        Pending Review
      </span>
    )
  }
  if (state === 'completed') {
    return (
      <span className="px-2 py-0.5 border-2 border-primary-container bg-primary-container/15 text-on-primary-container font-bold text-[10px] uppercase tracking-widest">
        Completed
      </span>
    )
  }
  if (state === 'cooldown') {
    const hoursLeft = cooldownEndsAt
      ? Math.max(1, Math.ceil((new Date(cooldownEndsAt).getTime() - Date.now()) / (60 * 60 * 1000)))
      : null
    return (
      <span className="px-2 py-0.5 border-2 border-on-background/30 font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">
        {hoursLeft ? `Again in ${hoursLeft}h` : 'On Cooldown'}
      </span>
    )
  }
  return null
}

function SubmitProofModal({ task, onClose, onSubmitted }) {
  const [proofUrl, setProofUrl] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const submit = async () => {
    if (!proofUrl.trim()) { setError('Paste a link to your post/comment as proof'); return }
    setLoading(true)
    setError(null)
    try {
      await submitTaskProof(task.id, proofUrl.trim())
      onSubmitted()
    } catch (err) {
      setError(err.message ?? 'Failed to submit proof')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-on-background/60 p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="bg-surface border-4 border-on-background w-full max-w-sm flex flex-col gap-4 p-6"
        style={{ boxShadow: '6px 6px 0px 0px var(--neo-border-color)' }}>
        <div className="flex items-center justify-between">
          <h2 className="font-extrabold text-[14px] uppercase tracking-widest">Submit Proof</h2>
          <button onClick={onClose}>
            <span className="material-symbols-outlined text-[20px]">close</span>
          </button>
        </div>

        <div>
          <p className="font-bold text-[13px]">{task.title}</p>
          <p className="text-[11px] text-on-surface-variant mt-1">{task.description}</p>
          {task.type === 'post_keyword' && task.requiredKeywords?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {task.requiredKeywords.map(kw => (
                <span key={kw} className="px-1.5 py-0.5 border-2 border-on-background/20 font-bold text-[9px] uppercase tracking-widest">
                  {kw}
                </span>
              ))}
            </div>
          )}
          {task.targetUrl && (
            <a href={task.targetUrl} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[11px] font-bold text-primary-container mt-2 hover:underline">
              <Icon name="open_in_new" className="text-[13px]" />
              Open target post
            </a>
          )}
        </div>

        {error && (
          <div className="border-2 border-error bg-error-container/40 text-on-error-container p-2 text-[11px] font-bold">
            {error}
          </div>
        )}

        <div className="flex flex-col gap-1">
          <label className="font-bold text-[10px] uppercase tracking-widest text-on-surface-variant">
            Link to your post/comment
          </label>
          <input
            type="url"
            value={proofUrl}
            onChange={e => setProofUrl(e.target.value)}
            placeholder="https://x.com/you/status/..."
            className="bg-surface border-2 border-on-background px-3 py-2 text-[13px] focus:outline-none focus:border-primary-container"
          />
          <p className="text-[10px] text-on-surface-variant/70 mt-0.5">
            This link can only be used once, on any task. Don't reuse an old post.
          </p>
        </div>

        <div className="flex gap-2 mt-1">
          <button onClick={onClose} disabled={loading}
            className="flex-1 px-4 py-2.5 border-2 border-on-background font-bold text-[11px] uppercase tracking-widest hover:bg-surface-container transition-colors disabled:opacity-50">
            Cancel
          </button>
          <button onClick={submit} disabled={loading}
            className="flex-1 px-4 py-2.5 border-2 border-primary-container bg-primary-container text-on-primary-container font-bold text-[11px] uppercase tracking-widest hover:brightness-105 transition-colors disabled:opacity-50">
            {loading ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  )
}

function TaskCard({ task, onSubmit }) {
  const meta = TYPE_META[task.type] ?? TYPE_META.custom
  const canSubmit = task.userState === 'available'

  return (
    <div className="border-2 border-on-background/20 bg-surface-container p-4 flex flex-col gap-2">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 flex items-center justify-center border-2 border-on-background/20 shrink-0">
            <Icon name={meta.icon} className="text-[16px]" />
          </span>
          <div>
            <p className="font-extrabold text-[13px] leading-tight">{task.title}</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">{meta.label}</p>
          </div>
        </div>
        <span className="font-bold text-[12px] text-primary-container shrink-0">+{task.rewardAmount} CPT</span>
      </div>

      <p className="text-[11px] text-on-surface-variant leading-snug">{task.description}</p>

      <div className="flex items-center justify-between gap-2 mt-1">
        <StateBadge state={task.userState} cooldownEndsAt={task.cooldownEndsAt} />
        {canSubmit && (
          <button
            onClick={() => onSubmit(task)}
            className="px-3 py-1.5 border-2 border-on-background font-bold text-[10px] uppercase tracking-widest hover:bg-on-background hover:text-surface transition-colors"
          >
            Submit Proof
          </button>
        )}
      </div>
    </div>
  )
}

function MySubmissionsHistory({ submissions }) {
  const [open, setOpen] = useState(false)
  if (submissions.length === 0) return null

  return (
    <div className="border-2 border-on-background/20 bg-surface-container-low">
      <button onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3">
        <span className="font-bold text-[11px] uppercase tracking-widest">
          My Submissions ({submissions.length})
        </span>
        <Icon name={open ? 'expand_less' : 'expand_more'} className="text-[18px]" />
      </button>
      {open && (
        <div className="flex flex-col gap-2 px-4 pb-4">
          {submissions.map(s => (
            <div key={s.id} className="flex items-center justify-between gap-2 border-t border-on-background/10 pt-2">
              <div className="min-w-0">
                <p className="text-[11px] font-mono truncate opacity-70">{s.proofUrl ?? 'Manually credited'}</p>
                {s.rejectionReason && (
                  <p className="text-[10px] text-error mt-0.5">{s.rejectionReason}</p>
                )}
              </div>
              <span className={`px-2 py-0.5 border-2 font-bold text-[9px] uppercase tracking-widest shrink-0 ${
                s.status === 'approved' ? 'border-primary-container text-on-primary-container bg-primary-container/15'
                : s.status === 'rejected' ? 'border-error text-error'
                : 'border-on-background/30 text-on-surface-variant'
              }`}>
                {s.status}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export function SocialTasksPanel() {
  const { user } = useAuth()
  const level = getLevelForCis(user?.cisScore ?? 0)
  const [tasks, setTasks] = useState([])
  const [submissions, setSubmissions] = useState([])
  const [loading, setLoading] = useState(true)
  const [modalTask, setModalTask] = useState(null)
  const [successMsg, setSuccessMsg] = useState(null)

  const load = useCallback(() => {
    setLoading(true)
    Promise.all([getActiveTasks(), getMyTaskSubmissions()])
      .then(([t, s]) => { setTasks(t); setSubmissions(s) })
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  const handleSubmitted = () => {
    setModalTask(null)
    setSuccessMsg('Proof submitted — an admin will review it soon.')
    setTimeout(() => setSuccessMsg(null), 4000)
    load()
  }

  return (
    <div className="flex flex-col gap-4">
      {modalTask && (
        <SubmitProofModal task={modalTask} onClose={() => setModalTask(null)} onSubmitted={handleSubmitted} />
      )}

      <div className="bg-on-background text-inverse-on-surface p-4 flex items-center gap-3">
        <Icon name="task_alt" className="text-[20px] opacity-80" />
        <div className="flex-1">
          <p className="text-[11px] leading-snug opacity-90">
            Complete tasks on X and submit a link as proof. Submissions are reviewed manually — rewards post once approved.
          </p>
          <Link to="/levels" className="text-[10px] font-bold uppercase tracking-widest text-primary-container hover:underline mt-1 inline-block">
            Your level: {level.label} · {formatMultiplier(level.multiplier)} rewards →
          </Link>
        </div>
      </div>

      {successMsg && (
        <div className="border-2 border-primary-container bg-primary-container/15 text-on-primary-container p-3 text-[11px] font-bold">
          {successMsg}
        </div>
      )}

      {loading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-24 bg-surface-container animate-pulse" />
          ))}
        </div>
      ) : tasks.length === 0 ? (
        <div className="border-2 border-dashed border-on-background/20 p-8 text-center">
          <p className="font-bold text-[12px] uppercase tracking-widest text-on-surface-variant">
            No tasks available right now — check back soon
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {tasks.map(task => (
            <TaskCard key={task.id} task={task} onSubmit={setModalTask} />
          ))}
        </div>
      )}

      <MySubmissionsHistory submissions={submissions} />
    </div>
  )
}